import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Toast from '../components/Toast';
import PieChart from '../components/PieChart';
import { formatCantitate } from '../utils/format';
import { formatNumarRaport, exportaCSV, printeazaTabel } from '../utils/export';
import RapoartePerioada from './RapoartePerioada';

const TABURI = [
  { id: 'activ', label: 'In productie' },
  { id: 'terminat', label: 'Produse terminate' },
  { id: 'raport', label: 'Raport rebuturi' },
  { id: 'rapoarte', label: 'Rapoarte perioada' }
];

export default function ProduseFinite() {
  const [tab, setTab] = useState('activ');
  const [toast, setToast] = useState(null);

  return (
    <div>
      <h1>Produse finite</h1>
      <p style={{ fontSize: 13, color: '#888', marginTop: -8 }}>
        Fiecare bucata produsa e un lot separat, cu costul lui propriu - un lot devine "produs terminat"
        abia cand e finalizat (fie intra pe stoc, fie e declarat rebut).
      </p>

      <div className="tabs">
        {TABURI.map(t => (
          <button key={t.id} type="button" className={`tab-btn${tab === t.id ? ' activ' : ''}`} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'activ' && <TabActiv setToast={setToast} />}
      {tab === 'terminat' && <TabTerminat setToast={setToast} />}
      {tab === 'raport' && <TabRaport setToast={setToast} />}
      {tab === 'rapoarte' && <RapoartePerioada setToast={setToast} />}

      <Toast message={toast?.message} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
}

// --- Tab "In productie" ---------------------------------------------------
function TabActiv({ setToast }) {
  const [loturi, setLoturi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [extins, setExtins] = useState(null);
  const [materiale, setMateriale] = useState([]);
  const [loadingMateriale, setLoadingMateriale] = useState(false);
  const [finalizeazaModal, setFinalizeazaModal] = useState(null); // { lotId, denumire }
  const [ffForm, setFfForm] = useState({ rezultat: 'REUSIT', observatii: '', dataDocument: '' });
  const [submitting, setSubmitting] = useState(false);

  function incarca() {
    setLoading(true);
    api.get('/api/loturi/active').then(res => setLoturi(res.data)).finally(() => setLoading(false));
  }

  useEffect(() => { incarca(); }, []);

  async function toggleExtins(lot) {
    if (extins === lot.id) {
      setExtins(null);
      return;
    }
    setExtins(lot.id);
    setLoadingMateriale(true);
    try {
      const res = await api.get(`/api/loturi/${lot.id}/materiale-consumate`);
      setMateriale(res.data);
    } finally {
      setLoadingMateriale(false);
    }
  }

  function deschideFinalizeaza(lot, e) {
    e.stopPropagation();
    setFfForm({ rezultat: 'REUSIT', observatii: '', dataDocument: new Date().toISOString().slice(0, 10) });
    setFinalizeazaModal({ lotId: lot.id, denumire: `${lot.cod ? lot.cod + ' - ' : ''}${lot.denumire}` });
  }

  async function confirmaFinalizeaza(e) {
    e.preventDefault();
    if (submitting) return;
    if (!ffForm.dataDocument) {
      setToast({ message: 'Data este obligatorie', type: 'error' });
      return;
    }
    const mesajConfirmare = ffForm.rezultat === 'REUSIT'
      ? 'Sigur finalizezi acest lot? Produsul va intra pe stoc.'
      : 'Sigur marchezi acest lot ca rebut? Nu va intra pe stoc.';
    if (!window.confirm(mesajConfirmare)) return;
    setSubmitting(true);
    try {
      await api.put(`/api/loturi/${finalizeazaModal.lotId}/finalizeaza`, {
        rezultat: ffForm.rezultat, observatii: ffForm.observatii || null, dataDocument: ffForm.dataDocument
      });
      setToast({ message: ffForm.rezultat === 'REUSIT' ? 'Lot finalizat - produs intrat pe stoc' : 'Lot marcat ca rebut', type: 'ok' });
      setFinalizeazaModal(null);
      setExtins(null);
      incarca();
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Eroare la finalizarea lotului', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  const EXPORT_COLOANE_ACTIV = [
    { key: 'client', label: 'Client' },
    { key: 'dataStart', label: 'Data start' },
    { key: 'produs', label: 'Produs' },
    { key: 'pretVanzareEstimat', label: 'Pret vanzare estimat' },
    { key: 'termenLivrare', label: 'Termen livrare' },
    { key: 'costAcumulat', label: 'Cost acumulat' }
  ];

  function randExportActiv(lot) {
    return {
      client: lot.client || '',
      dataStart: lot.dataStart ? new Date(lot.dataStart).toLocaleDateString('ro-RO') : '',
      produs: `${lot.cod ? lot.cod + ' - ' : ''}${lot.denumire}`,
      pretVanzareEstimat: formatNumarRaport(lot.pretVanzareEstimat),
      termenLivrare: lot.termenLivrare ? new Date(lot.termenLivrare).toLocaleDateString('ro-RO') : '',
      costAcumulat: formatNumarRaport(lot.costAcumulat)
    };
  }

  function exportaActiv() {
    exportaCSV('produse-in-productie.csv', EXPORT_COLOANE_ACTIV, loturi.map(randExportActiv));
  }

  function tipareseActiv() {
    printeazaTabel('Produse in productie', EXPORT_COLOANE_ACTIV.map(c => ({ ...c, dreapta: c.key !== 'client' && c.key !== 'produs' })), loturi.map(randExportActiv));
  }

  const totalActiv = loturi.reduce((acc, lot) => {
    acc.pretVanzareEstimat += Number(lot.pretVanzareEstimat) || 0;
    acc.costAcumulat += Number(lot.costAcumulat) || 0;
    return acc;
  }, { pretVanzareEstimat: 0, costAcumulat: 0 });

  if (loading) return <p>Se incarca...</p>;

  return (
    <div className="card">
      <div className="toolbar" style={{ justifyContent: 'flex-end' }}>
        <span style={{ fontSize: 13, color: '#444', marginRight: 'auto' }}>
          <strong>{loturi.length}</strong> loturi &middot; Total pret vanzare estimat: <strong>{formatCantitate(totalActiv.pretVanzareEstimat)} lei</strong> &middot; Total cost acumulat: <strong>{formatCantitate(totalActiv.costAcumulat)} lei</strong>
        </span>
        <button type="button" className="btn-csv" onClick={exportaActiv}>Export CSV</button>
        <button type="button" className="btn-print" onClick={tipareseActiv}>Print</button>
      </div>
      <table>
        <thead>
          <tr>
            <th></th>
            <th>Client</th>
            <th>Data start</th>
            <th>Produs</th>
            <th>Pret vanzare estimat</th>
            <th>Termen livrare</th>
            <th>Cost acumulat</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {loturi.map(lot => (
            <React.Fragment key={lot.id}>
              <tr onClick={() => toggleExtins(lot)} style={{ cursor: 'pointer' }}>
                <td>{extins === lot.id ? '\u25be' : '\u25b8'}</td>
                <td>{lot.client}</td>
                <td>{lot.dataStart ? new Date(lot.dataStart).toLocaleDateString('ro-RO') : '-'}</td>
                <td>
                  <strong>{lot.cod}</strong> - {lot.denumire}
                </td>
                <td>{lot.pretVanzareEstimat != null ? `${formatCantitate(lot.pretVanzareEstimat)} lei` : '-'}</td>
                <td>{lot.termenLivrare ? new Date(lot.termenLivrare).toLocaleDateString('ro-RO') : '-'}</td>
                <td>{lot.costAcumulat != null ? `${formatCantitate(lot.costAcumulat)} lei` : '-'}</td>
                <td>
                  <button type="button" className="btn" onClick={(e) => deschideFinalizeaza(lot, e)}>Finalizeaza</button>
                </td>
              </tr>
              {extins === lot.id && (
                <tr>
                  <td colSpan="8" style={{ background: '#f8f9fb', padding: '10px 16px' }}>
                    {loadingMateriale ? <p style={{ margin: 0 }}>Se incarca...</p> : materiale.length === 0 ? (
                      <p style={{ color: '#888', margin: 0 }}>Niciun material consumat inca pe acest lot.</p>
                    ) : (
                      <table>
                        <thead>
                          <tr><th>Data</th><th>Material</th><th>Cantitate</th><th>Pret unitar</th><th>Valoare</th><th>Utilizator</th></tr>
                        </thead>
                        <tbody>
                          {materiale.map(m => (
                            <tr key={m.id} style={m.inReteta ? undefined : { background: '#fdf3e7' }}>
                              <td>{new Date(m.data).toLocaleDateString('ro-RO')}</td>
                              <td>
                                {m.cod} - {m.denumire}
                                {!m.inReteta && <div style={{ color: '#c0392b', fontSize: 12, fontWeight: 600 }}>material extra-reteta</div>}
                              </td>
                              <td>{formatCantitate(m.cantitate)} {m.um}</td>
                              <td>{m.pretUnitar != null ? `${formatCantitate(m.pretUnitar)} lei` : '-'}</td>
                              <td>{m.valoareTotala != null ? `${formatCantitate(m.valoareTotala)} lei` : '-'}</td>
                              <td>{m.utilizatorNume || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
          {loturi.length === 0 && <tr><td colSpan="8">Niciun lot in lucru momentan.</td></tr>}
        </tbody>
      </table>

      {finalizeazaModal && (
        <div className="modal-overlay" onClick={() => setFinalizeazaModal(null)}>
          <div className="modal" style={{ width: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Finalizeaza lotul</h2>
              <button type="button" className="modal-close" onClick={() => setFinalizeazaModal(null)}>&times;</button>
            </div>
            <form onSubmit={confirmaFinalizeaza}>
              <div className="modal-body">
                <p style={{ marginTop: 0, color: '#888', fontSize: 13 }}>{finalizeazaModal.denumire}</p>
                <div className="field-row">
                  <div className="field">
                    <label>Rezultat *</label>
                    <select value={ffForm.rezultat} onChange={e => setFfForm({ ...ffForm, rezultat: e.target.value })}>
                      <option value="REUSIT">Reusit - intra pe stoc</option>
                      <option value="REBUT">Rebut - nu intra pe stoc</option>
                    </select>
                  </div>
                  <div className="field">
                    <label>Data *</label>
                    <input type="date" max={new Date().toISOString().slice(0, 10)}
                      value={ffForm.dataDocument} onChange={e => setFfForm({ ...ffForm, dataDocument: e.target.value })} required />
                  </div>
                </div>
                <div className="field-row">
                  <div className="field">
                    <label>Observatii{ffForm.rezultat === 'REBUT' ? ' (motiv rebut)' : ''}</label>
                    <input value={ffForm.observatii} onChange={e => setFfForm({ ...ffForm, observatii: e.target.value })}
                      placeholder={ffForm.rezultat === 'REBUT' ? 'ex: material defect, greseala croit...' : 'optional'} />
                  </div>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn secondary" onClick={() => setFinalizeazaModal(null)}>Anuleaza</button>
                <button type="submit" className="btn" disabled={submitting}>{submitting ? 'Se salveaza...' : 'Confirma'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Tab "Produse terminate" ----------------------------------------------
function TabTerminat({ setToast }) {
  const [loturi, setLoturi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [extins, setExtins] = useState(null);
  const [materiale, setMateriale] = useState([]);
  const [loadingMateriale, setLoadingMateriale] = useState(false);
  const [rebutModal, setRebutModal] = useState(null);
  const [iesireModal, setIesireModal] = useState(null); // { lotId, denumire }
  const [ieForm, setIeForm] = useState({ motiv: 'VANZARE', document: '', dataDocument: '' });
  const [aduInapoiModal, setAduInapoiModal] = useState(null); // { lotId, denumire }
  const [aiForm, setAiForm] = useState({ dataDocument: '' });
  const [submitting, setSubmitting] = useState(false);
  const [extinseCategorii, setExtinseCategorii] = useState(new Set(['stoc']));

  function toggleCategorie(cheie) {
    setExtinseCategorii(prev => {
      const nou = new Set(prev);
      if (nou.has(cheie)) nou.delete(cheie); else nou.add(cheie);
      return nou;
    });
  }

  function incarca() {
    setLoading(true);
    api.get('/api/loturi/terminate').then(res => setLoturi(res.data)).finally(() => setLoading(false));
  }

  useEffect(() => { incarca(); }, []);

  async function toggleExtins(lot) {
    if (extins === lot.id) {
      setExtins(null);
      return;
    }
    setExtins(lot.id);
    setLoadingMateriale(true);
    try {
      const res = await api.get(`/api/loturi/${lot.id}/materiale-consumate`);
      setMateriale(res.data);
    } finally {
      setLoadingMateriale(false);
    }
  }

  function deschideIesire(lot, e) {
    e.stopPropagation();
    setIeForm({ motiv: 'VANZARE', document: '', dataDocument: new Date().toISOString().slice(0, 10) });
    setIesireModal({ lotId: lot.id, denumire: `${lot.cod ? lot.cod + ' - ' : ''}${lot.denumire}` });
  }

  async function confirmaIesire(e) {
    e.preventDefault();
    if (submitting) return;
    if (!ieForm.dataDocument) {
      setToast({ message: 'Data este obligatorie', type: 'error' });
      return;
    }
    setSubmitting(true);
    try {
      await api.put(`/api/loturi/${iesireModal.lotId}/iesire`, {
        motiv: ieForm.motiv, document: ieForm.document || null, dataDocument: ieForm.dataDocument
      });
      setToast({ message: ieForm.motiv === 'VANZARE' ? 'Produs marcat ca vandut' : 'Produs marcat ca casat', type: 'ok' });
      setIesireModal(null);
      incarca();
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Eroare la inregistrarea iesirii', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  function deschideMarcheazaRebut(lot) {
    setRebutModal({ lotId: lot.id, observatii: '', dataDocument: new Date().toISOString().slice(0, 10) });
  }

  function deschideAduInapoi(lot, e) {
    e.stopPropagation();
    setAiForm({ dataDocument: new Date().toISOString().slice(0, 10) });
    setAduInapoiModal({ lotId: lot.id, denumire: `${lot.cod ? lot.cod + ' - ' : ''}${lot.denumire}` });
  }

  async function confirmaAduInapoi(e) {
    e.preventDefault();
    if (submitting) return;
    if (!aiForm.dataDocument) {
      setToast({ message: 'Data este obligatorie', type: 'error' });
      return;
    }
    setSubmitting(true);
    try {
      await api.put(`/api/loturi/${aduInapoiModal.lotId}/anuleaza-finalizare`, { dataDocument: aiForm.dataDocument });
      setToast({ message: 'Lot adus inapoi in productie', type: 'ok' });
      setAduInapoiModal(null);
      incarca();
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Eroare la anularea finalizarii', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  async function confirmaMarcheazaRebut(e) {
    e.preventDefault();
    if (!rebutModal.dataDocument) {
      setToast({ message: 'Data este obligatorie', type: 'error' });
      return;
    }
    try {
      await api.put(`/api/loturi/${rebutModal.lotId}/marcheaza-rebut`, {
        observatii: rebutModal.observatii || null, dataDocument: rebutModal.dataDocument
      });
      setToast({ message: 'Lot marcat ca rebut', type: 'ok' });
      setRebutModal(null);
      incarca();
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Eroare la marcarea ca rebut', type: 'error' });
    }
  }

  const CATEGORII = [
    { cheie: 'stoc', label: 'Pe stoc' },
    { cheie: 'vandut', label: 'Vandute' },
    { cheie: 'casat', label: 'Casate' },
    { cheie: 'rebut', label: 'Rebut' }
  ];

  function categorieLot(lot) {
    if (lot.rebut) return 'rebut';
    if (lot.iesireMotiv === 'VANZARE') return 'vandut';
    if (lot.iesireMotiv === 'CASARE_PIERDERE') return 'casat';
    return 'stoc';
  }

  const grupuriCategorii = CATEGORII.map(c => {
    const randuri = loturi.filter(l => categorieLot(l) === c.cheie);
    const totaluri = randuri.reduce((acc, lot) => {
      acc.pretVanzareEstimat += Number(lot.pretVanzareEstimat) || 0;
      acc.costAcumulat += Number(lot.costAcumulat) || 0;
      return acc;
    }, { pretVanzareEstimat: 0, costAcumulat: 0 });
    return { ...c, randuri, totaluri };
  });

  const EXPORT_COLOANE_TERMINAT = [
    { key: 'client', label: 'Client' },
    { key: 'dataStart', label: 'Data start' },
    { key: 'dataFinalizare', label: 'Data finalizare' },
    { key: 'produs', label: 'Produs' },
    { key: 'stare', label: 'Stare' },
    { key: 'pretVanzareEstimat', label: 'Pret vanzare estimat' },
    { key: 'termenLivrare', label: 'Termen livrare' },
    { key: 'costAcumulat', label: 'Cost' }
  ];

  function randExportTerminat(lot) {
    const stareLabel = CATEGORII.find(c => c.cheie === categorieLot(lot))?.label || '';
    return {
      client: lot.client || '',
      dataStart: lot.dataStart ? new Date(lot.dataStart).toLocaleDateString('ro-RO') : '',
      dataFinalizare: lot.dataFinalizare ? new Date(lot.dataFinalizare).toLocaleDateString('ro-RO') : '',
      produs: `${lot.cod ? lot.cod + ' - ' : ''}${lot.denumire}`,
      stare: stareLabel,
      pretVanzareEstimat: formatNumarRaport(lot.pretVanzareEstimat),
      termenLivrare: lot.termenLivrare ? new Date(lot.termenLivrare).toLocaleDateString('ro-RO') : '',
      costAcumulat: formatNumarRaport(lot.costAcumulat)
    };
  }

  function exportaCategorie(g) {
    exportaCSV(`produse-terminate-${g.cheie}.csv`, EXPORT_COLOANE_TERMINAT, g.randuri.map(randExportTerminat));
  }

  function tipareseCategorie(g) {
    printeazaTabel(`Produse terminate - ${g.label}`,
      EXPORT_COLOANE_TERMINAT.map(c => ({ ...c, dreapta: !['client', 'produs', 'stare'].includes(c.key) })),
      g.randuri.map(randExportTerminat));
  }

  function exportaTotTerminat() {
    exportaCSV('produse-terminate-total.csv', EXPORT_COLOANE_TERMINAT, loturi.map(randExportTerminat));
  }

  function tipareseTotTerminat() {
    printeazaTabel('Produse terminate - total',
      EXPORT_COLOANE_TERMINAT.map(c => ({ ...c, dreapta: !['client', 'produs', 'stare'].includes(c.key) })),
      loturi.map(randExportTerminat));
  }

  if (loading) return <p>Se incarca...</p>;

  return (
    <div className="card">
      <div className="toolbar" style={{ justifyContent: 'flex-end' }}>
        <span style={{ fontSize: 13, color: '#444', marginRight: 'auto' }}>
          <strong>{loturi.length}</strong> loturi in total
        </span>
        <button type="button" className="btn-csv" onClick={exportaTotTerminat}>Export CSV (total)</button>
        <button type="button" className="btn-print" onClick={tipareseTotTerminat}>Print (total)</button>
      </div>

      {grupuriCategorii.map(g => (
        <div key={g.cheie} style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 4px', borderBottom: '1px solid #eee' }}>
            <div onClick={() => toggleCategorie(g.cheie)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, flex: 1, flexWrap: 'wrap' }}>
              <span>{extinseCategorii.has(g.cheie) ? '\u25be' : '\u25b8'}</span>
              <span style={{ fontWeight: 600 }}>{g.label}</span>
              <span style={{ fontWeight: 400, color: '#888', fontSize: 13 }}>
                {g.randuri.length} loturi &middot; Pret vanzare estimat: <strong>{formatCantitate(g.totaluri.pretVanzareEstimat)} lei</strong> &middot; Cost: <strong>{formatCantitate(g.totaluri.costAcumulat)} lei</strong>
              </span>
            </div>
            <button type="button" className="btn-csv" onClick={() => exportaCategorie(g)}>Export CSV</button>
            <button type="button" className="btn-print" onClick={() => tipareseCategorie(g)}>Print</button>
          </div>

          {extinseCategorii.has(g.cheie) && (
            g.randuri.length === 0 ? (
              <p style={{ color: '#888', padding: '8px 4px' }}>Niciun lot in aceasta categorie.</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th></th>
                    <th>Client</th>
                    <th>Data start</th>
                    <th>Data finalizare</th>
                    <th>Produs</th>
                    <th>Pret vanzare estimat</th>
                    <th>Termen livrare</th>
                    <th>Cost</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {g.randuri.map(lot => (
                    <React.Fragment key={lot.id}>
                      <tr>
                        <td onClick={() => toggleExtins(lot)} style={{ cursor: 'pointer' }}>{extins === lot.id ? '\u25be' : '\u25b8'}</td>
                        <td onClick={() => toggleExtins(lot)} style={{ cursor: 'pointer' }}>{lot.client}</td>
                        <td onClick={() => toggleExtins(lot)} style={{ cursor: 'pointer' }}>{lot.dataStart ? new Date(lot.dataStart).toLocaleDateString('ro-RO') : '-'}</td>
                        <td onClick={() => toggleExtins(lot)} style={{ cursor: 'pointer' }}>{lot.dataFinalizare ? new Date(lot.dataFinalizare).toLocaleDateString('ro-RO') : '-'}</td>
                        <td onClick={() => toggleExtins(lot)} style={{ cursor: 'pointer' }}><strong>{lot.cod}</strong> - {lot.denumire}</td>
                        <td onClick={() => toggleExtins(lot)} style={{ cursor: 'pointer' }}>{lot.pretVanzareEstimat != null ? `${formatCantitate(lot.pretVanzareEstimat)} lei` : '-'}</td>
                        <td onClick={() => toggleExtins(lot)} style={{ cursor: 'pointer' }}>{lot.termenLivrare ? new Date(lot.termenLivrare).toLocaleDateString('ro-RO') : '-'}</td>
                        <td onClick={() => toggleExtins(lot)} style={{ cursor: 'pointer' }}>{lot.costAcumulat != null ? `${formatCantitate(lot.costAcumulat)} lei` : '-'}</td>
                        <td>
                          {!lot.rebut && !lot.iesireMotiv && (
                            <>
                              <button type="button" className="btn" onClick={(e) => deschideIesire(lot, e)}>Iesire</button>{' '}
                              <button type="button" className="btn secondary" onClick={() => deschideMarcheazaRebut(lot)}>Marcheaza rebut</button>{' '}
                              <button type="button" className="btn secondary" onClick={(e) => deschideAduInapoi(lot, e)}>Adu inapoi in productie</button>
                            </>
                          )}
                        </td>
                      </tr>
                      {extins === lot.id && (
                        <tr>
                          <td colSpan="9" style={{ background: '#f8f9fb', padding: '10px 16px' }}>
                            {lot.rebut && (
                              <p style={{ marginTop: 0 }}>
                                <strong>Observatii rebut:</strong> {lot.observatiiRebut || <span style={{ color: '#888' }}>fara observatii</span>}
                              </p>
                            )}
                            {loadingMateriale ? <p style={{ margin: 0 }}>Se incarca...</p> : materiale.length === 0 ? (
                              <p style={{ color: '#888', margin: 0 }}>Niciun material consumat pe acest lot.</p>
                            ) : (
                              <table>
                                <thead>
                                  <tr><th>Data</th><th>Material</th><th>Cantitate</th><th>Pret unitar</th><th>Valoare</th><th>Utilizator</th></tr>
                                </thead>
                                <tbody>
                                  {materiale.map(m => (
                                    <tr key={m.id} style={m.inReteta ? undefined : { background: '#fdf3e7' }}>
                                      <td>{new Date(m.data).toLocaleDateString('ro-RO')}</td>
                                      <td>
                                        {m.cod} - {m.denumire}
                                        {!m.inReteta && <div style={{ color: '#c0392b', fontSize: 12, fontWeight: 600 }}>material extra-reteta</div>}
                                      </td>
                                      <td>{formatCantitate(m.cantitate)} {m.um}</td>
                                      <td>{m.pretUnitar != null ? `${formatCantitate(m.pretUnitar)} lei` : '-'}</td>
                                      <td>{m.valoareTotala != null ? `${formatCantitate(m.valoareTotala)} lei` : '-'}</td>
                                      <td>{m.utilizatorNume || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )
          )}
        </div>
      ))}

      {rebutModal && (
        <div className="modal-overlay" onClick={() => setRebutModal(null)}>
          <div className="modal" style={{ width: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Marcheaza lotul ca rebut</h2>
              <button type="button" className="modal-close" onClick={() => setRebutModal(null)}>&times;</button>
            </div>
            <form onSubmit={confirmaMarcheazaRebut}>
              <div className="modal-body">
                <p style={{ marginTop: 0, color: '#888', fontSize: 13 }}>
                  Daca produsul e inca pe stoc, va fi scos automat (casare). Daca a fost deja livrat/vandut, operatia va fi blocata.
                </p>
                <div className="field-row">
                  <div className="field">
                    <label>Observatii (motiv rebut)</label>
                    <input value={rebutModal.observatii} onChange={e => setRebutModal({ ...rebutModal, observatii: e.target.value })}
                      placeholder="ex: defect descoperit la ambalare" />
                  </div>
                  <div className="field">
                    <label>Data *</label>
                    <input type="date" max={new Date().toISOString().slice(0, 10)}
                      value={rebutModal.dataDocument} onChange={e => setRebutModal({ ...rebutModal, dataDocument: e.target.value })} required />
                  </div>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn secondary" onClick={() => setRebutModal(null)}>Anuleaza</button>
                <button type="submit" className="btn">Confirma</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {iesireModal && (
        <div className="modal-overlay" onClick={() => setIesireModal(null)}>
          <div className="modal" style={{ width: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Iesire produs finit</h2>
              <button type="button" className="modal-close" onClick={() => setIesireModal(null)}>&times;</button>
            </div>
            <form onSubmit={confirmaIesire}>
              <div className="modal-body">
                <p style={{ marginTop: 0, color: '#888', fontSize: 13 }}>{iesireModal.denumire}</p>
                <div className="field-row">
                  <div className="field">
                    <label>Motiv *</label>
                    <select value={ieForm.motiv} onChange={e => setIeForm({ ...ieForm, motiv: e.target.value })}>
                      <option value="VANZARE">Vanzare</option>
                      <option value="CASARE_PIERDERE">Casare/Pierdere</option>
                    </select>
                  </div>
                  <div className="field">
                    <label>Data *</label>
                    <input type="date" max={new Date().toISOString().slice(0, 10)}
                      value={ieForm.dataDocument} onChange={e => setIeForm({ ...ieForm, dataDocument: e.target.value })} required />
                  </div>
                </div>
                <div className="field-row">
                  <div className="field">
                    <label>Document</label>
                    <input value={ieForm.document} onChange={e => setIeForm({ ...ieForm, document: e.target.value })}
                      placeholder={ieForm.motiv === 'VANZARE' ? 'nr. factura / aviz' : 'nr. PV de casare'} />
                  </div>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn secondary" onClick={() => setIesireModal(null)}>Anuleaza</button>
                <button type="submit" className="btn" disabled={submitting}>{submitting ? 'Se salveaza...' : 'Confirma'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {aduInapoiModal && (
        <div className="modal-overlay" onClick={() => setAduInapoiModal(null)}>
          <div className="modal" style={{ width: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Adu inapoi in productie</h2>
              <button type="button" className="modal-close" onClick={() => setAduInapoiModal(null)}>&times;</button>
            </div>
            <form onSubmit={confirmaAduInapoi}>
              <div className="modal-body">
                <p style={{ marginTop: 0, color: '#888', fontSize: 13 }}>{aduInapoiModal.denumire}</p>
                <p style={{ fontSize: 13 }}>
                  Produsul va fi scos din stocul de produse finite, iar lotul revine in tabul „In productie" -
                  costul materialelor deja consumate ramane legat de el.
                </p>
                <div className="field-row">
                  <div className="field">
                    <label>Data *</label>
                    <input type="date" max={new Date().toISOString().slice(0, 10)}
                      value={aiForm.dataDocument} onChange={e => setAiForm({ ...aiForm, dataDocument: e.target.value })} required />
                  </div>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn secondary" onClick={() => setAduInapoiModal(null)}>Anuleaza</button>
                <button type="submit" className="btn" disabled={submitting}>{submitting ? 'Se salveaza...' : 'Confirma'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Tab "Raport rebuturi" -------------------------------------------------
const CULOARE_REUSIT = '#2f9e5c';
const CULOARE_REBUT = '#d9534f';

function TabRaport() {
  const [randuri, setRanduri] = useState([]);
  const [totaluri, setTotaluri] = useState({ reusite: 0, rebuturi: 0, costReusite: 0, costRebuturi: 0 });
  const [loading, setLoading] = useState(true);
  const [dataInceput, setDataInceput] = useState('');
  const [dataSfarsit, setDataSfarsit] = useState('');
  const [selectat, setSelectat] = useState(null); // rand selectat din tabel (produs) sau null = toate
  const [detaliu, setDetaliu] = useState([]);
  const [loadingDetaliu, setLoadingDetaliu] = useState(false);

  function incarca() {
    setLoading(true);
    setSelectat(null);
    const params = new URLSearchParams();
    if (dataInceput) params.set('dataInceput', dataInceput);
    if (dataSfarsit) params.set('dataSfarsit', dataSfarsit);
    api.get(`/api/loturi/raport-rebuturi?${params.toString()}`).then(res => {
      setRanduri(res.data.produse);
      setTotaluri(res.data.totaluri);
    }).finally(() => setLoading(false));
  }

  useEffect(() => { incarca(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function toggleSelectat(r) {
    if (selectat?.produsFinitId === r.produsFinitId) {
      setSelectat(null);
      return;
    }
    setSelectat(r);
    setLoadingDetaliu(true);
    try {
      const params = new URLSearchParams();
      if (dataInceput) params.set('dataInceput', dataInceput);
      if (dataSfarsit) params.set('dataSfarsit', dataSfarsit);
      const res = await api.get(`/api/loturi/raport-rebuturi/${r.produsFinitId}?${params.toString()}`);
      setDetaliu(res.data);
    } finally {
      setLoadingDetaliu(false);
    }
  }

  // Sfera placintelor: produsul selectat, sau totalul general daca nimic ales.
  const sfera = selectat || totaluri;
  const sferaLabel = selectat ? `${selectat.cod ? selectat.cod + ' - ' : ''}${selectat.denumire}` : 'Toate produsele';

  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      <div className="card" style={{ flex: '0 0 75%', minWidth: 0 }}>
        <div className="field-row" style={{ marginBottom: 12 }}>
          <div className="field">
            <label>De la</label>
            <input type="date" value={dataInceput} onChange={e => setDataInceput(e.target.value)} />
          </div>
          <div className="field">
            <label>Pana la</label>
            <input type="date" value={dataSfarsit} onChange={e => setDataSfarsit(e.target.value)} />
          </div>
          <div className="field" style={{ alignSelf: 'flex-end' }}>
            <button type="button" className="btn" onClick={incarca}>Filtreaza</button>
          </div>
        </div>

        {loading ? <p>Se incarca...</p> : (
          <table>
            <thead>
              <tr><th></th><th>Produs</th><th>Pret vanzare estimat</th><th>Reusite</th><th>Rebuturi</th><th>Total</th><th>Rata rebut</th></tr>
            </thead>
            <tbody>
              {randuri.map(r => (
                <React.Fragment key={r.produsFinitId}>
                  <tr onClick={() => toggleSelectat(r)} style={{ cursor: 'pointer', fontWeight: selectat?.produsFinitId === r.produsFinitId ? 700 : 400 }}
                    className={Number(r.rataRebut) > 10 ? 'alerta' : ''}>
                    <td>{selectat?.produsFinitId === r.produsFinitId ? '\u25be' : '\u25b8'}</td>
                    <td><strong>{r.cod}</strong> - {r.denumire}</td>
                    <td>{r.pretVanzareEstimat != null ? `${formatCantitate(r.pretVanzareEstimat)} lei` : '-'}</td>
                    <td>{r.reusite}</td>
                    <td>{r.rebuturi}</td>
                    <td>{r.total}</td>
                    <td>{r.rataRebut}%</td>
                  </tr>
                  {selectat?.produsFinitId === r.produsFinitId && (
                    <tr>
                      <td colSpan="7" style={{ background: '#f8f9fb', padding: '10px 16px' }}>
                        {loadingDetaliu ? <p style={{ margin: 0 }}>Se incarca...</p> : detaliu.length === 0 ? (
                          <p style={{ color: '#888', margin: 0 }}>Niciun rebut in perioada selectata.</p>
                        ) : (
                          <table>
                            <thead><tr><th>Client</th><th>Data</th><th>Observatii</th></tr></thead>
                            <tbody>
                              {detaliu.map(d => (
                                <tr key={d.id}>
                                  <td>{d.client}</td>
                                  <td>{d.dataFinalizare ? new Date(d.dataFinalizare).toLocaleDateString('ro-RO') : '-'}</td>
                                  <td>{d.observatiiRebut || <span style={{ color: '#888' }}>fara observatii</span>}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {randuri.length === 0 && <tr><td colSpan="7">Niciun produs cu rebuturi in perioada selectata.</td></tr>}
            </tbody>
          </table>
        )}
      </div>

      <div className="card" style={{ flex: '1 1 25%', minWidth: 220 }}>
        <p style={{ margin: '0 0 12px', fontSize: 13, color: '#888' }}>
          {selectat ? sferaLabel : 'Nicio selectie \u2014 total pe toate produsele'}
        </p>
        <PieChart
          title="Reusite vs rebuturi (bucati)"
          slices={[
            { label: 'Reusite', value: sfera.reusite, color: CULOARE_REUSIT },
            { label: 'Rebuturi', value: sfera.rebuturi, color: CULOARE_REBUT }
          ]}
        />
        <div style={{ height: 24 }} />
        <PieChart
          title="Reusite vs rebuturi (cost materiale)"
          slices={[
            { label: 'Reusite', value: sfera.costReusite, color: CULOARE_REUSIT },
            { label: 'Rebuturi', value: sfera.costRebuturi, color: CULOARE_REBUT }
          ]}
          formatValue={v => `${formatCantitate(v)} lei`}
        />
      </div>
    </div>
  );
}

